import { useInView, motion } from "motion/react";
import { AnimVals } from '@/utils/animUtils';
import { toProxiedWpUrl } from '@/utils/wpMediaUrl';
import { useEffect, useMemo, useRef, useState } from "react";
import { SplideSlide } from "react-splide-ts";
import { usePostType } from "@/hooks/postType";
import { Button, TypePill, HeadlineAnim, Image, RhLink, CursorFollower } from "@components";
import { useDebug } from '@/utils/useDebugLog';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';

import className from "classnames/bind";
import styles from "./CarouselExhibitsInteractives.module.scss";
import { ExhibitData } from "../utils";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
const cx = className.bind(styles);

type Exhibit = ExhibitData;

type CarouselExhibitSlideProps = {
  exhibit: Exhibit;
  canPlay: boolean;
  active: boolean;
  index: number;
  isRevealEnabled: boolean;
  isInView: boolean;
};

export default function CarouselExhibitSlide({
  exhibit,
  canPlay,
  active,
  index,
  isRevealEnabled,
  isInView,
}: CarouselExhibitSlideProps) {
  const { mediaType, title, node, image, videoUrl, excerpt, uri, videoThumbnail, linkTarget, labelOverride } = exhibit;
  const { labels } = usePageWrapperData();
  const videoEl = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLAnchorElement | HTMLDivElement>(null);
  const videoInView = useInView(videoEl, { once: AnimVals.once, amount: 0.1 });
  const componentInView = useInView(containerRef, { once: AnimVals.once, amount: 0.1 });
  const isVideo = mediaType === "video";
  const postType = usePostType({ node });
  const ctaLabel = labelOverride || labels?.exhibitCarouselCta;
  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();

  // CursorFollower visibility control
  const [showCursorFollower, setShowCursorFollower] = useState(false);

  // Track if slide has animated in (only animate once)
  const hasAnimatedIn = useRef(false);

  // -------------------- Animate in

  const { log } = useDebug({ name: 'CarouselExhibitSlide', bgColor: 'lightgreen', enabled: false });

  const effectiveIsInView = useMemo(() => {
    const shouldShow = isInView && active;
    return shouldShow;
  }, [isInView, active]);
  const Tag = uri ? RhLink : "div";

  // Mark as animated once the animation should have completed
  useEffect(() => {
    if (effectiveIsInView && !hasAnimatedIn.current) {
      hasAnimatedIn.current = true;
    }
  }, [effectiveIsInView]);

  // Show cursor follower after slide is active and in view (desktop only)
  useEffect(() => {
    if (mounted && isReady && isDesktop && active && componentInView && uri) {
      setShowCursorFollower(true);
    } else {
      setShowCursorFollower(false);
    }
  }, [mounted, isReady, isDesktop, active, componentInView, uri]);

  const ANIM_DIST = AnimVals.sm.distance;
  const ANIM_OPACITY = AnimVals.opacity;
  const ANIM_EASE = AnimVals.ease;
  const ANIM_DUR = AnimVals.sm.duration;

  const createMotionProps = (delay: number = 0) => ({
    initial: isRevealEnabled ? {
      opacity: ANIM_OPACITY,
      y: ANIM_DIST
    } : {},
    animate: (effectiveIsInView || hasAnimatedIn.current) && isRevealEnabled ? {
      opacity: 1,
      y: 0
    } : {},
    transition: {
      duration: ANIM_DUR,
      ease: ANIM_EASE,
      delay
    },
  });

  // -------------------- Video

  useEffect(() => {
    if (videoInView && isVideo && videoEl.current) {
      videoEl.current.src = videoUrl || "";
    };
  }, [videoInView, videoEl]);

  useEffect(() => {
    if (active && canPlay) {
      videoEl.current?.play();
    } else {
      videoEl.current?.pause();
    }
  }, [canPlay, active]);

  // -------------------- Render

  return (
    <SplideSlide className={cx("slide")} aria-label={title}>
      <Tag href={uri} target={linkTarget || "_self"} className={cx("container")} ref={anchorRef as any}>
        <div className={cx("container-top")} ref={containerRef}>

          {isVideo ? (
            <video className={cx("video")}
              loop
              muted
              playsInline
              poster={toProxiedWpUrl(videoThumbnail?.node.mediaItemUrl)}
              ref={videoEl}
            />
          ) : (
            <Image className={cx("image")} image={image} />
          )}

          <div className={cx("container-top-content")}>

            {/* Pill */}
            { postType &&
              <motion.div {...createMotionProps(0)}>
                <TypePill className={cx("pill")}
                  type={postType.key}
                  copy={postType.label}
                />
              </motion.div>
            }

            {/* Headline */}
            <div className={cx("headline-wrapper")}>
              <HeadlineAnim
                tag="h3"
                text={title}
                className={cx(["display-xl", "slide-headline"])}
                isRevealEnabled={isRevealEnabled}
                isInView={effectiveIsInView || hasAnimatedIn.current}
                animInDelay={0.1}
                isCenterAlign={true}
                maxCharsPerWord={12}
              />
            </div>
          </div>
          { uri && (
            <>
              <motion.div className={cx("learn-more")} {...createMotionProps(0.3)}>
                <Button ariaLabel={title} label={ctaLabel} asDiv collapseOnMobile={true} />
              </motion.div>
              {mounted && isReady && isDesktop && (
                <CursorFollower
                  container={anchorRef}
                  circleText={ctaLabel}
                  isVisible={showCursorFollower}
                  mouseActive={showCursorFollower}
                />
              )}
          </>
        )}
        </div>

        {/* Body */}
        <motion.div className={cx("container-bottom")} {...createMotionProps(0.2)}>
          <p className={cx("text-m", "copy")} dangerouslySetInnerHTML={{ __html: excerpt }} />
        </motion.div>

      </Tag>
    </SplideSlide>
  );

}
